/** Board bar MUTATOR handlers for the pull-plan grid. Owns no geometry state
 *  (the proto* useState live in the component, feeding the undo snapshot +
 *  autosave); this hook receives those values/setters/deps and returns the
 *  drop / move / resize / recode / rename / delete + bulk handlers. */
import React from "react";
import type {
  DepEditOp,
  DepGraph,
  DraftPayload,
  ScopeMember,
  TrainState,
} from "../../../../models";
import {
  addTrainCar,
  addTrainDep,
  predTypeFromShort,
  removeTrainCar,
  setTrainCarPosition,
  updateTrainCarCode,
  updateTrainCarDuration,
} from "../../../../models";
import {
  addNewActivity,
  deleteActivities,
  stageDurationEdit,
  stageRenameEdit,
  stageTaktifications,
} from "../../draft/draftOps";
import { memberSpan, pruneScopeDecisions } from "../../ScopeLedger/laneState";
import type { BarSpan } from "../../WbsBoardView/WbsBoardView";
import { isTrainCarId, TRAIN_ID_BASE, TRAIN_LEAF_KEY } from "../train/trainReconcile";
import { makeId } from "../../../../utils/id";
import { barGraphId as barGraphIdOf, type PaletteActivity } from "../helpers";
import type { ProtoNewAct } from "../PullPlanGrid";

interface BulkDropIntent {
  ids: number[];
  dxDays: number;
  leafKey: string | null;
  lane: number | null;
  /** Per-bar target lane for a same-row vertical bulk move (activity_id → lane). */
  laneById?: Array<[number, number]>;
}

interface Args {
  palette: PaletteActivity[];
  protoMembers: ScopeMember[];
  scopeMembers: ScopeMember[] | null;
  protoNewActs: ProtoNewAct[];
  protoSpans: Map<number, BarSpan>;
  protoCalendar: string[];
  protoCalendarIndexFor: (iso: string | null) => number;
  protoTrain: TrainState | null;
  bulkDrop: BulkDropIntent | null;
  setBulkDrop: React.Dispatch<React.SetStateAction<BulkDropIntent | null>>;
  setProtoTrain: React.Dispatch<React.SetStateAction<TrainState | null>>;
  setProtoNewActs: React.Dispatch<React.SetStateAction<ProtoNewAct[]>>;
  setPendingOps: React.Dispatch<React.SetStateAction<DraftPayload>>;
  setProtoSpans: React.Dispatch<React.SetStateAction<Map<number, BarSpan>>>;
  setProtoLanes: React.Dispatch<React.SetStateAction<Map<number, number>>>;
  setProtoRowOverrides: React.Dispatch<React.SetStateAction<Map<number, string>>>;
  pushUndo: (label?: string) => void;
  requestDelete: (graphIds: string[]) => Promise<void>;
  removeProtoNewMany: (activityIds: number[]) => void;
  depGraphRef: React.MutableRefObject<DepGraph | null>;
}

export interface BoardMutators {
  protoSpanOf: (activityId: number) => BarSpan | null;
  stageProtoDrop: (
    leaf: { key: string; path: string[] },
    dayIndex: number,
    code: string,
    opts?: { lane?: number; predRef?: number | string; duration?: number },
  ) => string | null;
  handleProtoMove: (
    moves: Array<{
      activityId: number;
      startIndex: number;
      leafKey?: string;
      lane?: number;
    }>,
  ) => void;
  handleProtoResize: (activityId: number, durationDays: number) => void;
  handleProtoRecode: (
    activityId: number,
    draft: { code: string; name: string; color: string },
  ) => void;
  handleProtoRename: (activityId: number, name: string) => void;
  handleProtoDelete: (activityId: number) => void;
  pinLanes: (pairs: Array<[number, number]>) => void;
  deleteTrainMappedRefs: (refs: string[]) => void;
  barGraphId: (activityId: number) => string;
  moveBulk: (intent: {
    ids: number[];
    dxDays: number;
    leafKey: string | null;
    laneById?: Array<[number, number]>;
  }) => void;
  applyBulkMove: () => void;
  applyBulkDuplicate: () => void;
  handleBulkDrop: (intent: BulkDropIntent) => void;
}

export function useBoardMutators({
  palette,
  protoMembers,
  scopeMembers,
  protoNewActs,
  protoSpans,
  protoCalendar,
  protoCalendarIndexFor,
  protoTrain,
  bulkDrop,
  setBulkDrop,
  setProtoTrain,
  setProtoNewActs,
  setPendingOps,
  setProtoSpans,
  setProtoLanes,
  setProtoRowOverrides,
  pushUndo,
  requestDelete,
  removeProtoNewMany,
  depGraphRef,
}: Args): BoardMutators {
  // Stage a brand-new activity from a code drop (also the "Duplicate" path of
  // the conflict popup, and the future copy-level-sequence flow's create path).
  const stageProtoDrop = React.useCallback(
    (
      leaf: { key: string; path: string[] },
      dayIndex: number,
      code: string,
      opts?: { lane?: number; predRef?: number | string; duration?: number },
    ) => {
      const pal = palette.find((p) => p.activityKey === code);
      if (!pal) return null;
      if (leaf.key === TRAIN_LEAF_KEY) {
        // Drop/tap on the train lane appends the code as a new element, FS-tied
        // to the car it landed after (mirrors the board's lane-tap). Works on an
        // empty train too — that's how the first car gets added.
        if (!protoTrain) return null;
        pushUndo(`Add ${pal.activityKey} to train`);
        setProtoTrain((t) => {
          if (!t) return t;
          const before = new Set(t.cars.map((c) => c.carId));
          let next = addTrainCar(t, pal.activityKey, Math.max(1, pal.default_duration));
          const newCar = next.cars.find((c) => !before.has(c.carId));
          if (newCar) {
            // Land it at the tap/drop position (the selected lane's end), not at
            // the end of the whole train — so it sits right after the bar it's
            // FS-tied to and the FS-chain lane logic keeps it on that lane.
            next = setTrainCarPosition(next, newCar.carId, Math.max(0, dayIndex));
          }
          const predCarId =
            typeof opts?.predRef === "number"
              ? t.cars[TRAIN_ID_BASE - opts.predRef]?.carId
              : undefined;
          if (newCar && predCarId) {
            next = {
              ...next,
              deps: addTrainDep(next.deps, predCarId, newCar.carId, "FS", 0),
            };
          }
          return next;
        });
        return null;
      }
      const leafLabel = leaf.path[leaf.path.length - 1] ?? leaf.key;
      const wbsNodeId =
        (scopeMembers ?? []).find(
          (m) => (m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"]).join(" ") === leaf.key,
        )?.wbs_node_id ?? 0;
      pushUndo(`Add ${pal.activityKey}`);
      const draftId = makeId("new");
      const duration = Math.max(1, opts?.duration ?? pal.default_duration);
      const start = Math.max(0, Math.min(dayIndex, protoCalendar.length - duration));
      const name = `${pal.name} | ${leafLabel}`;
      setProtoNewActs((prev) => [
        ...prev,
        {
          draftId,
          wbsKey: leaf.key,
          wbsPath: leaf.path,
          wbsNodeId,
          startIndex: start,
          durationDays: duration,
          code: pal.activityKey,
          name,
          // Lane lives on the entry (read by protoLaneOverrides by index) — NOT a
          // protoLanes negId, which is stale when several are staged in one tick.
          ...(opts?.lane != null ? { lane: opts.lane } : {}),
        },
      ]);
      setPendingOps((prev) => {
        let next = addNewActivity(prev, {
          draft_id: draftId,
          name,
          takt_a: null,
          takt_b: null,
          takt_c: null,
          work_code: pal.activityKey,
          duration_days: duration,
        });
        // Tap-append ties the new bar FS to the lane's last bar.
        if (opts?.predRef != null) {
          next = {
            ...next,
            pending_dep_edits: [
              ...next.pending_dep_edits,
              {
                op: "add",
                pred_activity_id: opts.predRef,
                succ_activity_id: draftId,
                pred_type: "PR_FS",
                lag_hours: 0,
              },
            ],
          };
        }
        return next;
      });
      return draftId;
    },
    // setState setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [palette, scopeMembers, protoCalendar.length, protoTrain, pushUndo],
  );

  const protoSpanOf = React.useCallback(
    (activityId: number): BarSpan | null => {
      const o = protoSpans.get(activityId);
      if (o) return o;
      if (activityId < 0) {
        const n = protoNewActs[-activityId - 1];
        return n ? { startIndex: n.startIndex, durationDays: n.durationDays } : null;
      }
      const m = (scopeMembers ?? []).find((x) => x.activity_id === activityId);
      return m ? memberSpan(m, protoCalendarIndexFor) : null;
    },
    [protoSpans, protoNewActs, scopeMembers, protoCalendarIndexFor],
  );

  const handleProtoMove = React.useCallback(
    (moves: Array<{ activityId: number; startIndex: number; leafKey?: string; lane?: number }>) => {
      pushUndo(moves.length > 1 ? `Move ${moves.length}` : "Move");
      // Train-car drags set the element's STATIC position on the template.
      const trainMoves = moves.filter((mv) => isTrainCarId(mv.activityId));
      if (trainMoves.length > 0) {
        setProtoTrain((t) => {
          if (!t) return t;
          let next = t;
          for (const mv of trainMoves) {
            const carId = next.cars[TRAIN_ID_BASE - mv.activityId]?.carId;
            if (carId) next = setTrainCarPosition(next, carId, mv.startIndex);
          }
          return next;
        });
      }
      const newMoves = moves.filter(
        (mv) => mv.activityId < 0 && !isTrainCarId(mv.activityId),
      );
      const existing = moves.filter((mv) => mv.activityId >= 0);
      if (newMoves.length > 0) {
        setProtoNewActs((prev) =>
          prev.map((n, i) => {
            const mv = newMoves.find((x) => -x.activityId - 1 === i);
            if (!mv) return n;
            let rowPatch = {};
            if (mv.leafKey != null) {
              const m = (scopeMembers ?? []).find(
                (x) =>
                  (x.wbs_path.length > 0 ? x.wbs_path : ["(no WBS node)"]).join(" ") ===
                  mv.leafKey,
              );
              rowPatch = {
                wbsKey: mv.leafKey,
                wbsPath: m ? m.wbs_path : [mv.leafKey],
                wbsNodeId: m?.wbs_node_id ?? 0,
              };
            }
            return {
              ...n,
              startIndex: mv.startIndex,
              ...(mv.lane != null ? { lane: mv.lane } : {}),
              ...rowPatch,
            };
          }),
        );
      }
      if (existing.length > 0) {
        setProtoSpans((prev) => {
          const next = new Map(prev);
          for (const mv of existing) {
            const span = protoSpanOf(mv.activityId);
            if (!span) continue;
            next.set(mv.activityId, { ...span, startIndex: mv.startIndex });
          }
          return next;
        });
        setProtoRowOverrides((prev) => {
          const withRow = existing.filter((mv) => mv.leafKey != null);
          if (withRow.length === 0) return prev;
          const next = new Map(prev);
          for (const mv of withRow) next.set(mv.activityId, mv.leafKey as string);
          return next;
        });
        setProtoLanes((prev) => {
          const withLane = existing.filter((mv) => mv.lane != null);
          if (withLane.length === 0) return prev;
          const next = new Map(prev);
          for (const mv of withLane) next.set(mv.activityId, mv.lane as number);
          return next;
        });
      }
    },
    // setState setters + pushUndo are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoSpanOf, scopeMembers],
  );

  const handleProtoResize = React.useCallback(
    (activityId: number, durationDays: number) => {
      pushUndo("Duration");
      if (isTrainCarId(activityId)) {
        setProtoTrain((t) => {
          if (!t) return t;
          const carId = t.cars[TRAIN_ID_BASE - activityId]?.carId;
          return carId ? updateTrainCarDuration(t, carId, durationDays) : t;
        });
        return;
      }
      // Bound the duration to the remaining calendar axis (the drag-resize handle
      // already clamps; a typed popover value must too, or the staged duration
      // runs past the plan window while the bar renders clamped).
      const clampDur = (startIndex: number, d: number) =>
        Math.max(
          1,
          Math.min(d, Math.max(1, protoCalendar.length - Math.max(0, startIndex))),
        );
      if (activityId < 0) {
        setProtoNewActs((prev) => {
          const idx = -activityId - 1;
          const entry = prev[idx];
          if (!entry) return prev;
          const dur = clampDur(entry.startIndex, durationDays);
          setPendingOps((ops) => ({
            ...ops,
            pending_new_activities: ops.pending_new_activities.map((n) =>
              n.draft_id === entry.draftId ? { ...n, duration_days: dur } : n,
            ),
          }));
          return prev.map((n, i) =>
            i === idx ? { ...n, durationDays: dur } : n,
          );
        });
        return;
      }
      const span = protoSpanOf(activityId);
      if (!span) return;
      const dur = clampDur(span.startIndex, durationDays);
      setProtoSpans((prev) => {
        const next = new Map(prev);
        next.set(activityId, { ...span, durationDays: dur });
        return next;
      });
      setPendingOps((prev) => stageDurationEdit(prev, activityId, dur));
    },
    // setState setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoSpanOf, protoCalendar.length, pushUndo],
  );

  // Work-code change from the bar popover: unknown codes stage an add first,
  // committed bars stage a taktification, new bars patch their entry.
  const handleProtoRecode = React.useCallback(
    (activityId: number, draft: { code: string; name: string; color: string }) => {
      const code = draft.code.trim();
      if (!code) return;
      if (isTrainCarId(activityId)) {
        pushUndo("Work code");
        setProtoTrain((t) => {
          if (!t) return t;
          const carId = t.cars[TRAIN_ID_BASE - activityId]?.carId;
          return carId ? updateTrainCarCode(t, carId, code) : t;
        });
        return;
      }
      pushUndo("Work code");
      if (!palette.some((p) => p.activityKey === code)) {
        setPendingOps((prev) => ({
          ...prev,
          pending_work_code_adds: [
            ...prev.pending_work_code_adds,
            { code, prefix: draft.name || code, color: draft.color || null },
          ],
        }));
      }
      if (activityId < 0) {
        setProtoNewActs((prev) => {
          const idx = -activityId - 1;
          const entry = prev[idx];
          if (!entry) return prev;
          setPendingOps((ops) => ({
            ...ops,
            pending_new_activities: ops.pending_new_activities.map((n) =>
              n.draft_id === entry.draftId ? { ...n, work_code: code } : n,
            ),
          }));
          return prev.map((n, i) => (i === idx ? { ...n, code } : n));
        });
        return;
      }
      setPendingOps((prev) =>
        stageTaktifications(prev, [{ activity_id: activityId, takt_work: code }], []),
      );
    },
    // setState setters + pushUndo are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [palette],
  );

  // Delete the row activities a train element was driving — committed refs stage
  // a (draft-scoped) delete, draft refs are tombstoned — so removing an element
  // clears its work off every mapped row, not just the train lane. No modal.
  const deleteTrainMappedRefs = React.useCallback((refs: string[]) => {
    if (refs.length === 0) return;
    const committed = refs.filter((r) => /^\d+$/.test(r)).map(Number);
    const drafts = new Set(refs.filter((r) => !/^\d+$/.test(r)));
    if (committed.length > 0) {
      const g = depGraphRef.current;
      const edgeEndpoints = new Map<
        number,
        { pred: string | number; succ: string | number }
      >();
      if (g) {
        for (const [succId, edges] of Object.entries(g.edges)) {
          for (const e of edges) {
            if (e.edge_id != null) edgeEndpoints.set(e.edge_id, { pred: e.pred_id, succ: succId });
          }
        }
      }
      setPendingOps((prev) =>
        pruneScopeDecisions(deleteActivities(prev, committed, [], edgeEndpoints), committed),
      );
    }
    if (drafts.size > 0) {
      setProtoNewActs((prev) => {
        setPendingOps((ops) => ({
          ...ops,
          pending_new_activities: ops.pending_new_activities.filter(
            (n) => !drafts.has(n.draft_id),
          ),
        }));
        return prev.map((n) => (drafts.has(n.draftId) ? { ...n, deleted: true } : n));
      });
    }
    // setState setters + depGraphRef ref are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProtoDelete = React.useCallback(
    (activityId: number) => {
      if (isTrainCarId(activityId)) {
        const carId = protoTrain?.cars[TRAIN_ID_BASE - activityId]?.carId;
        if (!carId || !protoTrain) return;
        pushUndo("Remove train element");
        const next = removeTrainCar(protoTrain, carId);
        if (next.cars.length === 0) {
          // No train left to Apply — flush its deferred row removals now.
          deleteTrainMappedRefs(next.pendingRowDeletes ?? []);
          setProtoTrain(null);
        } else {
          // Row removals are deferred to Apply (carried on pendingRowDeletes).
          setProtoTrain(next);
        }
        return;
      }
      if (activityId < 0) {
        pushUndo("Delete");
        removeProtoNewMany([activityId]);
      } else {
        void requestDelete([String(activityId)]);
      }
    },
    // setProtoTrain setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [removeProtoNewMany, requestDelete, pushUndo, protoTrain, deleteTrainMappedRefs],
  );

  const handleProtoRename = React.useCallback((activityId: number, name: string) => {
    pushUndo("Rename");
    if (activityId < 0) {
      setProtoNewActs((prev) => {
        const idx = -activityId - 1;
        const entry = prev[idx];
        if (!entry) return prev;
        setPendingOps((ops) => ({
          ...ops,
          pending_new_activities: ops.pending_new_activities.map((n) =>
            n.draft_id === entry.draftId ? { ...n, name } : n,
          ),
        }));
        return prev.map((n, i) => (i === idx ? { ...n, name } : n));
      });
      return;
    }
    setPendingOps((prev) => stageRenameEdit(prev, activityId, name));
    // setState setters + pushUndo are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Freeze a set of bars at their CURRENT lane (committed → protoLanes, drafts →
  // their .lane). The board is a canvas: a drag pins every bar in the touched
  // row(s) here first, so moving one bar never re-packs the rest. Lane-only — no
  // span write, no undo push (it just records where things already sit).
  const pinLanes = React.useCallback((pairs: Array<[number, number]>) => {
    const committed = pairs.filter(([id]) => id >= 0);
    const drafts = pairs.filter(([id]) => id < 0 && !isTrainCarId(id));
    if (committed.length > 0) {
      setProtoLanes((prev) => {
        const next = new Map(prev);
        for (const [id, lane] of committed) next.set(id, lane);
        return next;
      });
    }
    if (drafts.length > 0) {
      const laneByIdx = new Map(drafts.map(([id, lane]) => [-id - 1, lane]));
      setProtoNewActs((prev) =>
        prev.map((n, i) => (laneByIdx.has(i) ? { ...n, lane: laneByIdx.get(i) as number } : n)),
      );
    }
    // setState setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const barGraphId = React.useCallback(
    (activityId: number): string => barGraphIdOf(protoNewActs, activityId),
    [protoNewActs],
  );

  const moveBulk = React.useCallback(
    (intent: {
      ids: number[];
      dxDays: number;
      leafKey: string | null;
      laneById?: Array<[number, number]>;
    }) => {
      const laneMap = intent.laneById ? new Map(intent.laneById) : null;
      const moves: Array<{
        activityId: number;
        startIndex: number;
        leafKey?: string;
        lane?: number;
      }> = [];
      for (const id of intent.ids) {
        const span = protoSpanOf(id);
        if (!span) continue;
        const lane = laneMap?.get(id);
        moves.push({
          activityId: id,
          startIndex: Math.max(0, span.startIndex + intent.dxDays),
          ...(intent.leafKey != null ? { leafKey: intent.leafKey } : {}),
          ...(lane != null ? { lane } : {}),
        });
      }
      if (moves.length > 0) handleProtoMove(moves);
    },
    [protoSpanOf, handleProtoMove],
  );

  const applyBulkMove = React.useCallback(() => {
    if (!bulkDrop) return;
    const intent = bulkDrop;
    setBulkDrop(null);
    moveBulk(intent);
    // setBulkDrop setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkDrop, moveBulk]);

  const applyBulkDuplicate = React.useCallback(() => {
    const intent = bulkDrop;
    if (!intent) return;
    setBulkDrop(null);
    const entries: Array<{
      draftId: string;
      wbsKey: string;
      wbsPath: string[];
      wbsNodeId: number;
      startIndex: number;
      durationDays: number;
      code: string;
      name: string;
    }> = [];
    const draftIdByOriginal = new Map<number, string>();
    for (const id of intent.ids) {
      const m = protoMembers.find((x) => x.activity_id === id);
      const span = protoSpanOf(id);
      if (!m || !span) continue;
      const ownKey = (m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"]).join(" ");
      const leafKey = intent.leafKey ?? ownKey;
      const home = (scopeMembers ?? []).find(
        (x) =>
          (x.wbs_path.length > 0 ? x.wbs_path : ["(no WBS node)"]).join(" ") === leafKey,
      );
      // The copy reads by the leaf it lands in: keep the work name (before the
      // first "|"), swap the suffix to the TARGET leaf's label.
      const leafLabel =
        home && home.wbs_path.length > 0
          ? home.wbs_path[home.wbs_path.length - 1]
          : leafKey;
      const workName = m.task_name.split("|")[0].trim() || m.task_name;
      const draftId = makeId("new");
      draftIdByOriginal.set(id, draftId);
      entries.push({
        draftId,
        wbsKey: leafKey,
        wbsPath: home ? home.wbs_path : [leafKey],
        wbsNodeId: home?.wbs_node_id ?? 0,
        startIndex: Math.max(0, span.startIndex + intent.dxDays),
        durationDays: span.durationDays,
        code: m.takt_work,
        name: `${workName} | ${leafLabel}`,
      });
    }
    if (entries.length === 0) return;
    // The copies carry the chunk's INTERNAL logic: every tie between two
    // duplicated activities is cloned onto their copies (same type/lag).
    // Ties to work outside the chunk stay on the originals only.
    const tieAdds: DepEditOp[] = [];
    const g = depGraphRef.current;
    if (g) {
      const gidToDraft = new Map<string, string>();
      draftIdByOriginal.forEach((draftId, origId) => {
        gidToDraft.set(barGraphId(origId), draftId);
      });
      for (const [succGid, edges] of Object.entries(g.edges)) {
        const succDraft = gidToDraft.get(succGid);
        if (!succDraft) continue;
        for (const e of edges) {
          const predDraft = gidToDraft.get(e.pred_id);
          if (!predDraft) continue;
          tieAdds.push({
            op: "add",
            pred_activity_id: predDraft,
            succ_activity_id: succDraft,
            pred_type: predTypeFromShort(e.type),
            lag_hours: e.lag_hours,
          });
        }
      }
    }
    pushUndo(`Duplicate ${entries.length}`);
    setProtoNewActs((prev) => [...prev, ...entries]);
    setPendingOps((prev) => {
      let next = prev;
      for (const e of entries) {
        next = addNewActivity(next, {
          draft_id: e.draftId,
          name: e.name,
          takt_a: null,
          takt_b: null,
          takt_c: null,
          work_code: e.code || null,
          duration_days: e.durationDays,
        });
      }
      if (tieAdds.length > 0) {
        next = { ...next, pending_dep_edits: [...next.pending_dep_edits, ...tieAdds] };
      }
      return next;
    });
    // setState setters + depGraphRef ref are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkDrop, protoMembers, protoSpanOf, scopeMembers, pushUndo, barGraphId]);

  // Bulk drop dispatcher: a SAME-LEVEL move (dropped back on the selection's
  // own leaf, or nowhere new) just moves — no move-vs-duplicate question. The
  // modal is only for crossing into a DIFFERENT level/section.
  const handleBulkDrop = React.useCallback(
    (intent: BulkDropIntent) => {
      // The train lane is not a real WBS leaf — never move activities into it.
      if (intent.leafKey === TRAIN_LEAF_KEY) {
        moveBulk({ ...intent, leafKey: null });
        return;
      }
      const leafOf = (id: number) => {
        const m = protoMembers.find((x) => x.activity_id === id);
        return m ? (m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"]).join(" ") : null;
      };
      const sameLevel =
        intent.leafKey == null ||
        intent.ids.every((id) => leafOf(id) === intent.leafKey);
      if (sameLevel) {
        moveBulk(intent);
        return;
      }
      setBulkDrop(intent);
    },
    // setBulkDrop setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoMembers, moveBulk],
  );

  return {
    protoSpanOf,
    stageProtoDrop,
    handleProtoMove,
    handleProtoResize,
    handleProtoRecode,
    handleProtoRename,
    handleProtoDelete,
    pinLanes,
    deleteTrainMappedRefs,
    barGraphId,
    moveBulk,
    applyBulkMove,
    applyBulkDuplicate,
    handleBulkDrop,
  };
}
